import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { PROGRAMAS, detectarRuta, camposDesdeURL } from "./programs";

// ============================================================================
//  ChatBot — Encuesta conversacional (multi-programa).
//
//  Las definiciones de cada programa viven en ./programs/*.js.
//  Este archivo solo tiene la UI del chat y la resolución de la ruta.
//
//  Qué programa/tipo se completa se resuelve en este orden:
//    1. props            → <ChatBot programa="eop" tipo="fin" />
//    2. path             → /chatbot/{programa}/{tipo}   (ej: /chatbot/eop/fin)
//    3. query string     → ?programa=eop&tipo=fin
//    4. compatibilidad   → /chatbot/pre , /chatbot/post  (programa "domino")
//    5. default          → programa por defecto, tipo por defecto
//
//  Tipos de paso: "text", "radio" y "cascade" (departamento → localidad).
//  Los steps "text" pueden traer una función `validate` (ver programs/validaciones.js).
//
//  Se puede corregir la última respuesta con el botón "↩ Corregir":
//  quita la última respuesta registrada y vuelve a preguntar ese paso.
//
//  Al responder la última pregunta, el envío se dispara automáticamente
//  (no hay botón intermedio). Si el envío falla, aparece "Reintentar envío".
// ===========================================================================


// Hook: true cuando el ancho de pantalla es de móvil (<= 600px).
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function ChatBot({
  proxyBase = "https://pideapi2025.azurewebsites.net/api/FormAssemblyProxy/submit",
  programa,  // "domino" | "eop" — si no viene, se detecta de la URL
  tipo,      // "pre" | "post" | "fin" — si no viene, se detecta de la URL
  formId,    // opcional: fuerza un formId distinto al de la config
}) {
  const isMobile = useIsMobile();

  // Config de la encuesta activa (se resuelve una sola vez).
  const cfg = useMemo(() => {
    const auto = detectarRuta();
    const prog = (programa && PROGRAMAS[programa]) ? programa : auto.programa;
    const progCfg = PROGRAMAS[prog];
    const t = (tipo && progCfg.tipos[tipo]) ? tipo : auto.tipo;
    return { ...progCfg.tipos[t], programa: prog, tipo: t };
  }, [programa, tipo]);

  const STEPS = cfg.steps;
  const idFormulario = formId || cfg.formId;

  // Campos pre-cargados desde la URL (ej: ?tfa_4675=a14Pn00000F78cvIAB).
  // Se combinan con los defaults del programa y se envían sin preguntarlos.
  const DEFAULTS = useMemo(
    () => ({ ...cfg.defaults, ...camposDesdeURL() }),
    [cfg.defaults]
  );

  const [answers, setAnswers] = useState({ ...DEFAULTS });
  const [mensajes, setMensajes] = useState([]);
  const [stepActual, setStepActual] = useState(null);
  const [modoTexto, setModoTexto] = useState(false);
  const [textoInput, setTextoInput] = useState("");
  const [finalizado, setFinalizado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");

  // Estado del campo en cascada (departamento → localidad).
  const [cascadeFase, setCascadeFase] = useState(null); // null | "dep" | "loc"
  const [cascadeDep, setCascadeDep] = useState(null);    // departamento elegido
  const [cascadeFiltro, setCascadeFiltro] = useState("");

  const submitUrl = `${proxyBase}/${idFormulario}`;

  const msgsRef = useRef(null);
  const inputRef = useRef(null);
  const arrancoRef = useRef(false);

  // Orden en que se respondieron los steps, para poder "corregir el último".
  // Cada entrada: { stepId, campos } donde `campos` son las claves de
  // answers que hay que borrar al deshacer (en cascade son dos).
  const ordenRef = useRef([]);

  const addMsg = (texto, who) => setMensajes((prev) => [...prev, { texto, who }]);

  // progreso
  const hechos = STEPS.filter((s) => answers[s.id] !== undefined).length;
  const pct = STEPS.length ? Math.round((hechos / STEPS.length) * 100) : 0;
  const contador = finalizado
    ? "¡Todo listo!"
    : `Pregunta ${Math.min(hechos + 1, STEPS.length)} de ${STEPS.length}`;

  // ¿Se puede corregir? Sí mientras haya al menos una respuesta registrada y
  // no se esté enviando/ya enviado.
  const puedeCorregir = ordenRef.current.length > 0 && !enviando && !enviado;

  // Envía las respuestas al proxy. Recibe `ans` por parámetro porque, cuando
  // se dispara de forma automática al terminar, el estado `answers` todavía no
  // refleja la última respuesta en ese tick de render.
  const enviar = useCallback(async (ans) => {
    const datos = ans || answers;
    setEnviando(true);
    setErrorEnvio("");
    try {
      const fd = new FormData();
      Object.entries(datos).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(submitUrl, { method: "POST", body: fd });
      const data = await res.json();
      if (res.ok && data.success) {
        setEnviado(true);
      } else {
        throw new Error(data.message || "Error desconocido");
      }
    } catch (err) {
      setErrorEnvio(err.message || "Error al enviar");
      addMsg("❌ Hubo un error al enviar: " + (err.message || "") + ". Por favor intentá de nuevo.", "bot");
    } finally {
      setEnviando(false);
    }
  }, [answers, submitUrl]);

  const nextStep = useCallback((ans) => {
    const s = STEPS.find((x) => ans[x.id] === undefined);
    if (!s) {
      setStepActual(null);
      setModoTexto(false);
      setFinalizado(true);
      setTimeout(() => {
        addMsg(cfg.cierre, "bot");
        enviar(ans); // ← envío automático al responder la última pregunta
      }, 200);
      return;
    }
    setTimeout(() => {
      addMsg(s.label, "bot");
      setStepActual(s);
      if (s.type === "text") {
        setModoTexto(true);
        setTextoInput("");
        setTimeout(() => inputRef.current?.focus(), 50);
      } else if (s.type === "cascade") {
        setModoTexto(false);
        setCascadeFase("dep");
        setCascadeDep(null);
        setCascadeFiltro("");
      } else {
        setModoTexto(false);
      }
    }, 200);
  }, [STEPS, cfg.cierre, enviar]);

  useEffect(() => {
    if (arrancoRef.current) return;
    arrancoRef.current = true;
    addMsg(cfg.saludo, "bot");
    setTimeout(() => nextStep({ ...DEFAULTS }), 600);
  }, [nextStep, cfg.saludo, DEFAULTS]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [mensajes]);

  const elegirOpcion = (s, opt) => {
    const nuevas = { ...answers, [s.id]: opt.val };
    setAnswers(nuevas);
    ordenRef.current.push({ stepId: s.id, campos: [s.id] });
    addMsg(opt.label, "user");
    setStepActual(null);
    setTimeout(() => nextStep(nuevas), 350);
  };

  const handleText = () => {
    const val = textoInput.trim();
    if (!val || !stepActual) return;

    // Validación opcional por step (genérica). Si `validate` devuelve un
    // string, es el mensaje de error: se muestra y el usuario reintenta.
    if (typeof stepActual.validate === "function") {
      const error = stepActual.validate(val);
      if (error) {
        addMsg(val, "user");
        addMsg("⚠️ " + error, "bot");
        setTextoInput("");
        setTimeout(() => inputRef.current?.focus(), 50);
        return;
      }
    }

    const nuevas = { ...answers, [stepActual.id]: val };
    const campos = [stepActual.id];
    // El post pide confirmar el correo: se completa solo con el mismo valor.
    if (stepActual.id === "tfa_4963") { nuevas.tfa_4988 = val; campos.push("tfa_4988"); }
    setAnswers(nuevas);
    ordenRef.current.push({ stepId: stepActual.id, campos });
    addMsg(val, "user");
    setModoTexto(false);
    setTextoInput("");
    setTimeout(() => nextStep(nuevas), 350);
  };

  // Paso 1 del campo en cascada: elegir departamento.
  const elegirDepartamento = (dep) => {
    addMsg(dep.label, "user");
    setCascadeDep(dep);
    setCascadeFiltro("");
    setCascadeFase("loc");
  };

  // Paso 2: elegir localidad → se guardan ambos valores y se avanza.
  const elegirLocalidad = (s, dep, loc) => {
    addMsg(loc.label, "user");
    const nuevas = {
      ...answers,
      [s.id]: dep.val,        // departamento (tfa_1998)
      [dep.locField]: loc.val, // localidad (campo dependiente, ej. tfa_3388)
    };
    setAnswers(nuevas);
    ordenRef.current.push({ stepId: s.id, campos: [s.id, dep.locField] });
    setCascadeFase(null);
    setCascadeDep(null);
    setCascadeFiltro("");
    setStepActual(null);
    setTimeout(() => nextStep(nuevas), 350);
  };

  // Corrige la última respuesta: la quita de answers y vuelve a preguntar
  // ese paso. Funciona para text, radio y cascade (borra ambos campos).
  const corregirUltimo = () => {
    const ultimo = ordenRef.current.pop();
    if (!ultimo) return;

    const nuevas = { ...answers };
    ultimo.campos.forEach((c) => { delete nuevas[c]; });
    setAnswers(nuevas);

    // Si estábamos en el estado "finalizado" (última respondida), volvemos.
    setFinalizado(false);
    setErrorEnvio("");

    // Reseteamos el modo de entrada actual; nextStep reabre el paso correcto.
    setStepActual(null);
    setModoTexto(false);
    setTextoInput("");
    setCascadeFase(null);
    setCascadeDep(null);
    setCascadeFiltro("");

    addMsg("Volvamos atrás para corregir esa respuesta 👇", "bot");
    setTimeout(() => nextStep(nuevas), 250);
  };

  return (
    <div style={{ ...S.body, ...(isMobile ? S.bodyMobile : {}) }}>
      <div style={{ ...S.wrap, ...(isMobile ? S.wrapMobile : {}) }}>
        <div style={S.header}>
          <h1 style={S.h1}>{PROGRAMAS[cfg.programa]?.label || cfg.titulo}</h1>
          <p style={S.sub}>{cfg.subtitulo} · {contador}</p>
        </div>
        <div style={S.progressBar}>
          <div style={{ ...S.progressFill, width: (enviado ? 100 : pct) + "%" }} />
        </div>

        <div ref={msgsRef} style={S.messages}>
          {enviado ? (
            <div style={S.successWrap}>
              <div style={S.successIcon}>✅</div>
              <h2 style={S.successH2}>¡Evaluación enviada!</h2>
              <p style={S.successP}>{cfg.exito}</p>
            </div>
          ) : (
            mensajes.map((m, i) => (
              <div key={i} style={{ ...S.msg, ...(isMobile ? S.msgMobile : {}), ...(m.who === "user" ? S.msgUser : S.msgBot) }}>
                {m.texto}
              </div>
            ))
          )}
        </div>

        {/* opciones (radio) */}
        {!enviado && !modoTexto && !finalizado && stepActual && stepActual.type === "radio" && (
          <div style={{ ...S.optionsArea, ...(isMobile ? S.optionsAreaMobile : {}) }}>
            {stepActual.options.map((opt) => (
              <button key={opt.val} style={{ ...S.optBtn, ...(isMobile ? S.optBtnMobile : {}) }}
                onClick={() => elegirOpcion(stepActual, opt)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f0faf6"; e.currentTarget.style.borderColor = "#1D9E75"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#ddd"; }}>
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* campo en cascada: departamento → localidad */}
        {!enviado && !finalizado && stepActual && stepActual.type === "cascade" && cascadeFase && (() => {
          const esDep = cascadeFase === "dep";
          const lista = esDep
            ? stepActual.cascade.departamentos
            : (cascadeDep?.localidades || []);
          const filtro = cascadeFiltro.trim().toLowerCase();
          const filtrada = filtro
            ? lista.filter((o) => o.label.toLowerCase().includes(filtro))
            : lista;
          return (
            <div style={{ ...S.cascadeArea, ...(isMobile ? S.optionsAreaMobile : {}) }}>
              <input
                style={{ ...S.textInput, ...(isMobile ? S.textInputMobile : {}), marginBottom: 6 }}
                value={cascadeFiltro}
                placeholder={esDep ? "Buscá tu departamento..." : "Buscá tu localidad..."}
                onChange={(e) => setCascadeFiltro(e.target.value)}
                autoFocus
              />
              <div style={S.cascadeList}>
                {filtrada.length === 0 && (
                  <div style={S.cascadeEmpty}>Sin resultados</div>
                )}
                {filtrada.map((opt) => (
                  <button key={opt.val} style={{ ...S.optBtn, ...(isMobile ? S.optBtnMobile : {}) }}
                    onClick={() => esDep ? elegirDepartamento(opt) : elegirLocalidad(stepActual, cascadeDep, opt)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f0faf6"; e.currentTarget.style.borderColor = "#1D9E75"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#ddd"; }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* corregir para modos sin barra propia (radio / cascade) */}
        {puedeCorregir && !modoTexto && !finalizado && stepActual &&
          (stepActual.type === "radio" || stepActual.type === "cascade") && (
          <div style={S.correctBar}>
            <button
              style={{ ...S.correctBtn, ...(isMobile ? S.correctBtnMobile : {}) }}
              onClick={corregirUltimo}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#E7F4F4")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              ↩ Corregir anterior
            </button>
          </div>
        )}

        {/* texto */}
        {!enviado && modoTexto && stepActual && (
          <div style={S.textArea}>
            {puedeCorregir && (
              <button style={{ ...S.correctBtn, ...(isMobile ? S.correctBtnMobile : {}) }}
                onClick={corregirUltimo} title="Corregir la respuesta anterior"
                onMouseEnter={(e) => (e.currentTarget.style.background = "#E7F4F4")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                ↩
              </button>
            )}
            <input
              ref={inputRef}
              style={{ ...S.textInput, ...(isMobile ? S.textInputMobile : {}) }}
              value={textoInput}
              placeholder={stepActual.placeholder || "Escribí tu respuesta..."}
              onChange={(e) => setTextoInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleText(); }}
            />
            <button style={{ ...S.sendBtn, ...(isMobile ? S.sendBtnMobile : {}) }} onClick={handleText}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#0F6E56")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#1D9E75")}>
              Enviar
            </button>
          </div>
        )}

        {/* envío automático: estado / reintento en caso de error */}
        {!enviado && finalizado && (
          <div style={S.submitArea}>
            {enviando ? (
              <p style={S.hint}>Enviando tus respuestas de forma segura...</p>
            ) : errorEnvio ? (
              <>
                <button style={S.submitBtn} onClick={() => enviar(answers)}>
                  Reintentar envío
                </button>
                <p style={S.hint}>No se pudo enviar. Volvé a intentarlo.</p>
              </>
            ) : (
              <p style={S.hint}>Procesando...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  body: {
    fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
    background: "#EEF4F5",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  bodyMobile: { padding: 0, minHeight: "100dvh", alignItems: "stretch" },
  wrap: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 560,
    height: 620,
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 8px 30px rgba(53,105,122,.18)",
  },
  wrapMobile: { maxWidth: "100%", height: "100dvh", borderRadius: 0, boxShadow: "none" },
  header: { padding: "16px 20px", background: "#2AA8A8", borderBottom: "none" },
  h1: { fontSize: 15, fontWeight: 600, color: "#fff", margin: 0 },
  sub: { fontSize: 12, color: "rgba(255,255,255,.8)", marginTop: 2 },
  progressBar: { height: 4, background: "#DDE8EA" },
  progressFill: { height: "100%", background: "#35697A", transition: "width 0.4s ease" },
  messages: {
    flex: 1, overflowY: "auto", padding: 16,
    display: "flex", flexDirection: "column", gap: 10, background: "#F7FAFA",
  },
  msg: { maxWidth: "82%", padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-line" },
  msgMobile: { maxWidth: "88%", fontSize: 15, padding: "11px 15px" },
  msgBot: { background: "#E7F4F4", color: "#35697A", alignSelf: "flex-start", borderBottomLeftRadius: 4 },
  msgUser: { background: "#2AA8A8", color: "#fff", alignSelf: "flex-end", borderBottomRightRadius: 4 },
  correctBar: { padding: "8px 16px", borderTop: "1px solid #E5ECEE", display: "flex", justifyContent: "center" },
  correctBtn: {
    background: "transparent", border: "1px solid #C9D9DD", borderRadius: 8,
    padding: "8px 14px", fontSize: 13, color: "#35697A", cursor: "pointer",
    whiteSpace: "nowrap", transition: "background .2s ease",
  },
  correctBtnMobile: { padding: "12px 14px", fontSize: 14, minHeight: 48 },
  optionsArea: { padding: "8px 16px 10px", display: "flex", flexDirection: "column", gap: 6, maxHeight: 230, overflowY: "auto" },
  cascadeArea: { padding: "8px 16px 10px", display: "flex", flexDirection: "column", gap: 6, maxHeight: 260 },
  cascadeList: { display: "flex", flexDirection: "column", gap: 6, overflowY: "auto", maxHeight: 210, WebkitOverflowScrolling: "touch" },
  cascadeEmpty: { fontSize: 13, color: "#6F8B95", padding: "8px 4px", textAlign: "center" },
  optionsAreaMobile: { padding: "10px 14px 14px", gap: 8, maxHeight: "45vh", WebkitOverflowScrolling: "touch" },
  optBtn: {
    background: "#fff", border: "1px solid #35697A", borderRadius: 10,
    padding: "9px 14px", fontSize: 13, color: "#35697A", cursor: "pointer",
    textAlign: "left", transition: "all .2s ease",
  },
  optBtnMobile: { padding: "13px 16px", fontSize: 15, lineHeight: 1.4, minHeight: 48 },
  textArea: { padding: "10px 14px", borderTop: "1px solid #E5ECEE", display: "flex", gap: 8 },
  textInput: {
    flex: 1, border: "1px solid #C9D9DD", borderRadius: 8,
    padding: "8px 12px", fontSize: 14, color: "#35697A", outline: "none",
  },
  textInputMobile: { fontSize: 16, padding: "12px 14px", minHeight: 48 },
  sendBtn: {
    background: "#35697A", color: "#fff", border: "none", borderRadius: 8,
    padding: "8px 18px", cursor: "pointer", fontSize: 14, fontWeight: 500, transition: "background .2s ease",
  },
  sendBtnMobile: { padding: "12px 18px", fontSize: 15, minHeight: 48 },
  submitArea: { padding: "14px 16px", borderTop: "1px solid #E5ECEE", display: "flex", flexDirection: "column", gap: 8 },
  submitBtn: {
    background: "#35697A", color: "#fff", border: "none", borderRadius: 10,
    padding: 13, fontSize: 15, fontWeight: 700, cursor: "pointer", width: "100%", transition: "all .2s ease",
  },
  submitBtnOff: { background: "#C9C9C9", color: "#777", cursor: "not-allowed" },
  hint: { fontSize: 12, color: "#6F8B95", textAlign: "center" },
  successWrap: { textAlign: "center", padding: "24px 20px" },
  successIcon: { fontSize: 48 },
  successH2: { fontSize: 18, fontWeight: 600, marginTop: 12, color: "#35697A" },
  successP: { fontSize: 14, color: "#5E7680", marginTop: 6 },
};